import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Layers as LayersIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const MaskGallery = ({ masks, selectedIndex, isLoading, stage, totalMasks, displayedMasks, modelName = 'YOLO' }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  
  const handleImageClick = (mask) => {
    setViewerImage(mask);
    setViewerOpen(true);
  };
  
  // Reorder masks to put selected one first after selection
  const displayMasks = useMemo(() => {
    if (!masks || masks.length === 0) return [];
    
    // After selection is complete, move selected mask to first position
    if (selectedIndex > 0 && (stage === 'cropping' || stage === 'complete')) {
      const masksArray = [...masks];
      const selectedMask = masksArray.find(m => m.index === selectedIndex);
      if (selectedMask) {
        const otherMasks = masksArray.filter(m => m.index !== selectedIndex);
        return [selectedMask, ...otherMasks];
      }
    }
    return masks;
  }, [masks, selectedIndex, stage]);
  
  // Check if mask is selected
  const isSelected = (mask) => mask.index === selectedIndex;
  
  // Check if we should show selection state
  const showSelection = stage === 'selecting' || stage === 'cropping' || stage === 'complete';
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Paper
          sx={{
            p: 3,
            background: 'rgba(30, 41, 59, 0.6)',
            backdropFilter: 'blur(10px)',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LayersIcon sx={{ mr: 1, color: 'secondary.main' }} />
            <Typography variant="h6">
              Segmentation Masks
            </Typography>
            {masks && masks.length > 0 && (
              <Chip
                label={`${masks.length} masks`}
                size="small"
                sx={{ ml: 2 }}
              />
            )}
            {isLoading && (
              <Chip
                label="Generating..."
                size="small"
                sx={{ ml: 2 }}
                color="primary"
              />
            )}
          </Box>
          
          {isLoading ? (
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 1 }}>
              {[...Array(6)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  width={150}
                  height={150}
                  sx={{ borderRadius: 2, flexShrink: 0 }}
                />
              ))}
            </Box>
          ) : (
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                overflowX: 'auto', 
                py: 1,
                '&::-webkit-scrollbar': {
                  height: 8
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'rgba(148, 163, 184, 0.1)',
                  borderRadius: 4
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'rgba(148, 163, 184, 0.3)',
                  borderRadius: 4,
                  '&:hover': {
                    bgcolor: 'rgba(148, 163, 184, 0.5)'
                  }
                }
              }}
            >
              <AnimatePresence mode="sync">
                {displayMasks && displayMasks.length > 0 ? displayMasks.map((mask, index) => (
                  <motion.div
                    key={mask.index}
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ 
                      opacity: showSelection && !isSelected(mask) ? 0.5 : 1, 
                      scale: isSelected(mask) && stage === 'selecting' ? 1.1 : 1,
                      y: 0,
                      x: isSelected(mask) && (stage === 'cropping' || stage === 'complete') ? -index * 30 : 0
                    }}
                    transition={{ 
                      delay: index * 0.08,
                      duration: 0.4,
                      type: "spring",
                      stiffness: 100
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    <motion.div
                      animate={{
                        scale: isSelected(mask) && stage === 'selecting' ? [1, 1.05, 1] : 1,
                      }}
                      transition={{
                        duration: 0.5,
                        repeat: isSelected(mask) && stage === 'selecting' ? Infinity : 0,
                        repeatDelay: 1
                      }}
                    >
                      <Box
                        onClick={() => handleImageClick(mask)}
                        sx={{
                          position: 'relative',
                          width: 150,
                          height: 150,
                          cursor: 'pointer',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '3px solid',
                          borderColor: isSelected(mask) && showSelection
                            ? 'success.main' 
                            : 'transparent',
                          transition: 'all 0.3s ease',
                          boxShadow: isSelected(mask) && showSelection
                            ? '0 0 20px rgba(16, 185, 129, 0.5)' 
                            : 'none',
                          // Checkerboard pattern to show transparency
                          backgroundImage: `
                            linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                            linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                            linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                            linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
                          `,
                          backgroundSize: '10px 10px',
                          backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                      <img
                        src={mask.preview}
                        alt={`Mask ${mask.index}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                      
                      <Chip
                        label={`#${mask.index}`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: isSelected(mask) && showSelection ? 'success.main' : 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      
                      {isSelected(mask) && showSelection && (
                        <>
                          <CheckCircleIcon 
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              color: 'success.main',
                              bgcolor: 'white',
                              borderRadius: '50%',
                              fontSize: 28
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'success.main',
                              py: 0.5,
                              display: 'flex',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                              AI Selected
                            </Typography>
                          </Box>
                        </>
                      )}
                      </Box>
                    </motion.div>
                  </motion.div>
                )) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {stage === 'segmenting' ? 'Processing image...' : 'No masks available'}
                  </Typography>
                )}
              </AnimatePresence>
            </Box>
          )}
          
          {masks && masks.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Generated {totalMasks || masks.length} masks
              </Typography>
              
              {stage === 'selecting' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={14} sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Selecting best mask...
                  </Typography>
                </Box>
              )}
              
              {selectedIndex > 0 && (stage === 'cropping' || stage === 'complete') && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CheckIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                    Mask #{selectedIndex} was selected by AI
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>
      
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setViewerOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {viewerImage && (
            <Box>
              <img
                src={viewerImage.preview}
                alt={`Mask ${viewerImage.index}`}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
              
              <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6">
                  Mask #{viewerImage.index}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Area: {viewerImage.area} pixels
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bounding box: {viewerImage.bbox?.join(', ')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MaskGallery;